// src/components/Header.tsx
//
// Product menu matches processor-close: pack they post and pay.
// Sample preview uses the $80 / $79.60 walkthrough, labeled sample.

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProfile } from '../types';
import { mockSignOut } from '../services/firebase';
import {
  Factory,
  User as UserIcon,
  Repeat,
  Wallet,
  ShieldCheck,
  Menu as MenuIcon,
} from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
  NavigationMenuPositioner,
} from './ui/navigation-menu';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { Button } from './ui/button';

interface HeaderProps {
  user: UserProfile | null;
}

const PRODUCT_LINKS = [
  { label: 'Reconciliation', description: 'What cleared. What still hangs.', icon: Repeat, path: '/#how-it-works' },
  { label: 'Funding', description: 'The net the processor will move.', icon: Wallet, path: '/#night-run' },
  { label: 'Exceptions', description: 'The rows that need a person.', icon: ShieldCheck, path: '/#night-run' },
];

const Header: React.FC<HeaderProps> = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await mockSignOut();
    navigate('/');
  };

  const handleNavClick = (path: string) => {
    setMobileOpen(false);
    if (path.startsWith('/#')) {
      const sectionId = path.substring(2);
      if (location.pathname === '/') {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } else {
      navigate(path);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-brand-surface border-b border-brand-border">
      <div className="max-w-screen-xl mx-auto h-full px-6 flex items-center justify-between">

        <div
          className="flex items-center gap-2 cursor-pointer group select-none"
          onClick={() => navigate('/')}
          aria-label="Five Dollar Workday"
        >
          <Factory className="w-5 h-5 text-brand-black transition-transform duration-300 group-hover:rotate-12" strokeWidth={1.5} />
          <span className="text-sm font-semibold tracking-tight text-brand-black">FDW</span>
        </div>

        <NavigationMenu orientation="horizontal" className="hidden lg:flex">
          <NavigationMenuList className="flex items-center gap-1">

            <NavigationMenuItem>
              <NavigationMenuTrigger className="h-9 px-3 rounded-lg text-sm text-brand-charcoal hover:text-brand-black transition-colors">
                Product
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="flex gap-6 p-5 w-[600px] max-w-[calc(100vw-40px)] rounded-2xl">
                  <div className="flex-1 flex flex-col gap-4">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-1">
                        Product
                      </p>
                      <p className="text-sm font-medium text-brand-black">Daily processor close</p>
                      <p className="text-xs text-brand-gray font-light mt-1">
                        We finish the Marqeta or Lithic night. You get the pack.
                        You post it and pay it.
                      </p>
                    </div>
                    <div className="flex flex-col gap-3">
                      {PRODUCT_LINKS.map((item) => (
                        <NavigationMenuLink
                          key={item.label}
                          onClick={() => handleNavClick(item.path)}
                          className="text-left cursor-pointer"
                        >
                          <span className="text-sm font-medium text-brand-black block">{item.label}</span>
                          <span className="text-xs text-brand-gray font-light">{item.description}</span>
                        </NavigationMenuLink>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-brand-border text-xs text-brand-gray font-mono">
                      <span>Marqeta · Lithic</span>
                    </div>
                  </div>
                  <div className="w-[220px] shrink-0 rounded-xl border border-brand-border bg-white p-4 text-xs font-mono text-brand-charcoal">
                    <p className="text-brand-gray mb-2">Mar 28 · sample</p>
                    <p>Cleared $80.00</p>
                    <p>Net $79.60</p>
                    <p className="text-brand-accent mt-2">2 need a person</p>
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink
                onClick={() => handleNavClick('/#how-it-works')}
                className="flex items-center h-9 px-3 rounded-lg text-sm text-brand-charcoal hover:text-brand-black transition-colors cursor-pointer"
              >
                How it works
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink
                onClick={() => handleNavClick('/security')}
                className="flex items-center h-9 px-3 rounded-lg text-sm text-brand-charcoal hover:text-brand-black transition-colors cursor-pointer"
              >
                Security
              </NavigationMenuLink>
            </NavigationMenuItem>

          </NavigationMenuList>
          <NavigationMenuPositioner align="start" sideOffset={12} />
        </NavigationMenu>

        <div className="flex items-center gap-3">
          {user ? (
            <div
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-brand-surface rounded-full border border-brand-border cursor-pointer hover:bg-brand-border transition-colors"
              onClick={() => navigate('/dashboard')}
            >
              <div className="w-5 h-5 bg-brand-black rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                {user.displayName ? user.displayName[0].toUpperCase() : <UserIcon size={10} />}
              </div>
              <span className="hidden sm:inline text-xs font-semibold text-brand-black max-w-[80px] truncate">
                {user.displayName || 'User'}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                className="text-[10px] font-bold uppercase tracking-wider text-brand-charcoal hover:text-red-600 ml-1"
              >
                Sign out
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={() => navigate('/login?next=/chat&intent=program-map')}
                className="hidden sm:block text-sm text-brand-charcoal hover:text-brand-black transition-colors"
              >
                Sign in
              </button>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/signup?next=/chat&intent=program-map')}
                className="hidden sm:inline-flex"
              >
                Get a program map
              </Button>
            </>
          )}

          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <Drawer open={mobileOpen} onOpenChange={setMobileOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Menu</DrawerTitle>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-1">
            {PRODUCT_LINKS.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.path)}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-brand-surface transition-colors"
              >
                <item.icon className="w-4 h-4 text-brand-black shrink-0" strokeWidth={1.5} />
                <span className="text-sm text-brand-black">{item.label}</span>
              </button>
            ))}
            <button
              onClick={() => handleNavClick('/#how-it-works')}
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-brand-surface transition-colors"
            >
              <span className="text-sm text-brand-black">How it works</span>
            </button>
            <button
              onClick={() => handleNavClick('/security')}
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-brand-surface transition-colors"
            >
              <span className="text-sm text-brand-black">Security</span>
            </button>

            <div className="pt-4 mt-2 border-t border-brand-border flex flex-col gap-2">
              {user ? (
                <button
                  onClick={() => { setMobileOpen(false); navigate('/dashboard'); }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-brand-surface"
                >
                  <div className="w-6 h-6 bg-brand-black rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                    {user.displayName ? user.displayName[0].toUpperCase() : <UserIcon size={10} />}
                  </div>
                  <span className="text-sm font-medium text-brand-black">{user.displayName || 'Dashboard'}</span>
                </button>
              ) : (
                <>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => { setMobileOpen(false); navigate('/signup?next=/chat&intent=program-map'); }}
                  >
                    Get a program map
                  </Button>
                  <button
                    onClick={() => { setMobileOpen(false); navigate('/login?next=/chat&intent=program-map'); }}
                    className="w-full text-sm text-brand-charcoal py-2"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </header>
  );
};

export default Header;